import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { airhorn, fireConfetti } from "@/lib/fun";

const STAFF = [
  { ext: "201", color: "#f59e0b" },
  { ext: "210", color: "#ec4899" },
  { ext: "203", color: "#10b981" },
  { ext: "202", color: "#06b6d4" },
];

const londonTime = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });

export const fmtTalk = (s) => {
  if (!s) return "0m";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  return m ? `${m}m` : `${s}s`;
};

const Num = ({ label, value, testid }) => (
  <div className="shrink-0 text-right">
    <p className="font-mono text-[1.35em] font-bold leading-none" data-testid={testid}>{value}</p>
    <p className="mt-[0.25em] text-[0.55em] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
  </div>
);

const londonNow = () => {
  const [h, m] = new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" }).split(":").map(Number);
  return h + m / 60;
};
const dayFraction = () => Math.min(1, Math.max(0, (londonNow() - 9) / 8));

const status = (calls, target, w) => {
  if (!target?.calls) return null;
  const frac = dayFraction();
  if (frac <= 0.05) return { behind: false, pct: calls / target.calls };
  const expected = w.pace_mode ? target.calls * frac : target.calls;
  return { behind: calls < Math.floor(expected), pct: calls / target.calls };
};

const StaffCard = ({ ext, color, d, w, top }) => {
  const last = d?.lastCall?.startedAt;
  const s = w && d ? status(d.callsToday || 0, w.targets?.[ext], w) : null;
  const bar = s ? (s.behind ? w.behind_color : w.ahead_color) : color;
  return (
    <div className={`relative flex min-w-0 items-center gap-[0.7em] overflow-hidden rounded-[0.6em] border bg-card/80 px-[0.7em] py-[0.4em] ${s?.behind && w.behind_flash ? "flash-behind" : ""}`} style={{ boxShadow: `inset 0.22em 0 0 ${bar}`, borderColor: s?.behind ? `${w.behind_color}88` : undefined }} data-testid={`call-card-${ext}`} data-behind={s?.behind ? "1" : "0"}>
      <div className="min-w-[3.6em] flex-1 whitespace-nowrap">
        <p className="font-display text-[1.3em] font-bold leading-tight tracking-tight">{top && <span className="crown-bob mr-[0.2em]" data-testid={`call-crown-${ext}`}>👑</span>}{d?.name || "…"}</p>
        <p className="text-[0.6em] text-muted-foreground">Last <span className="font-mono text-foreground/80" data-testid={`call-last-${ext}`}>{last ? londonTime(last) : "–"}</span>{w?.targets?.[ext]?.calls ? <> · target <span className="font-mono text-foreground/80" data-testid={`call-target-${ext}`}>{w.targets[ext].calls}</span></> : null}</p>
      </div>
      <Num label="Talk time" value={fmtTalk(d?.talkTimeSeconds)} testid={`call-talk-${ext}`} />
      <Num label="Calls" value={d?.callsToday ?? "–"} testid={`call-count-${ext}`} />
      {s && <span className="absolute bottom-0 left-0 h-[0.15em] transition-[width]" style={{ width: `${Math.min(100, s.pct * 100)}%`, background: bar }} />}
    </div>
  );
};

const useTargetParty = (data, w) => {
  const prev = useRef({});
  useEffect(() => {
    if (!data || !w) return;
    const day = new Date().toISOString().slice(0, 10);
    STAFF.forEach(({ ext }) => {
      const calls = data[ext]?.callsToday || 0;
      const t = w.targets?.[ext]?.calls;
      const key = `wb-hit-${day}-${ext}`;
      if (t && calls >= t && !localStorage.getItem(key)) {
        localStorage.setItem(key, "1");
        if (prev.current[ext] !== undefined) {
          if (w.celebrate) fireConfetti("fireworks");
          if (w.airhorn) airhorn();
        }
      }
      prev.current[ext] = calls;
    });
  }, [data, w]);
};

export const CallStats = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const w = useSettings()?.team?.wallboard;
  useTargetParty(data, w);
  const best = data ? Math.max(0, ...STAFF.map((s) => data[s.ext]?.callsToday || 0)) : 0;

  useEffect(() => {
    const load = () => api.get("/netsapiens/call-summary").then((r) => { setData(r.data); setError(false); }).catch(() => setError(true));
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative" data-testid="call-stats" title={error ? "Phone stats unavailable, retrying" : "Calls today"}>
      {error && <span className="absolute -left-2 -top-1 h-2 w-2 rounded-full bg-red-500" data-testid="call-stats-error" />}
      <div className="grid gap-[0.45em]" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(11.5em, 1fr))" }}>
        {STAFF.map((s) => <StaffCard key={s.ext} {...s} d={data?.[s.ext]} w={w} top={!!w?.crown && best > 0 && data?.[s.ext]?.callsToday === best} />)}
      </div>
    </section>
  );
};
